import React, { useState, useEffect, useCallback } from 'react';
import { fetchContent, updateSection, uploadFile, fetchGallery, uploadGalleryImage, deleteGalleryImage, reorderGallery, rotateGalleryImage } from '../api';
import { FONT_LIBRARY, FONT_WEIGHTS, TEXT_TRANSFORMS, SECTION_TEXT_ELEMENTS, DEFAULT_TYPO, preloadAllFonts } from '../typographyConfig';
import './Admin.css';

// Bumped whenever the admin panel ships a meaningful change.
// Use this in production to confirm whether the deployed bundle is current.
// If you don't see "typography-cms" listed here in production, you're on a stale cached bundle.
const ADMIN_BUILD = 'v2026-02-29.gallery-frozen-v16';

// Persistent admin-side BroadcastChannel. Keeping it open (rather than
// creating/closing per save) is important — closing immediately after
// postMessage can drop the message before it's delivered in some browsers.
let _adminBC = null;
function _getAdminBC() {
  if (_adminBC) return _adminBC;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      _adminBC = new BroadcastChannel('circle-studios-cms');
    }
  } catch (e) { /* ignore */ }
  return _adminBC;
}

// Broadcast a "content-updated" event so any open public-site tab re-fetches
// and re-renders immediately (no refresh required). Uses 3 channels for
// maximum reliability:
//   1. BroadcastChannel — instant, same-origin, all modern browsers
//   2. localStorage storage event — backup for same-origin cross-tab
//   3. The backend will ALSO push via SSE to every connected tab (including
//      different browsers / devices). That's handled by the server on every
//      PUT /api/content/{section} — see `_notify_content_updated` in server.py.
function broadcastContentUpdated(section) {
  const payload = { type: 'content-updated', section, ts: Date.now() };
  try {
    const bc = _getAdminBC();
    if (bc) bc.postMessage(payload);
  } catch (e) { /* ignore */ }
  try {
    // Writing to localStorage fires a 'storage' event in every OTHER tab.
    // Use a unique value each time so the event fires reliably.
    localStorage.setItem('circle-studios-cms-updated', JSON.stringify(payload));
  } catch (e) { /* ignore */ }
  // eslint-disable-next-line no-console
  console.log('[Circle Studios Admin] Broadcast save:', payload);
}

const TABS = [
  { key: 'hero', label: 'Hero' },
  { key: 'trustedPartners', label: 'Trusted Partners' },
  { key: 'eventLogos', label: 'Event / Location Logos' },
  { key: 'about', label: 'About' },
  { key: 'capabilities', label: 'Capabilities' },
  { key: 'gallery', label: 'Our Work' },
  { key: 'sustainability', label: 'Sustainability' },
  { key: 'proprietary', label: 'Proprietary Engagements' },
  { key: 'contact', label: 'Contact' },
  { key: 'footer', label: 'Footer' },
  { key: 'typography', label: 'Typography' },
];

export default function AdminPanel({ token, onLogout }) {
  const [content, setContent] = useState(null);
  const [activeTab, setActiveTab] = useState('hero');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const loadContent = useCallback(async () => {
    try {
      const data = await fetchContent();
      setContent(data);
    } catch (err) {
      setError('Failed to load content');
    }
  }, []);

  useEffect(() => { loadContent(); }, [loadContent]);

  const saveSection = async (section, data) => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      await updateSection(token, section, data);
      // Broadcast to any open public-site tabs so they refetch content instantly.
      broadcastContentUpdated(section);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(`Failed to save ${section}`);
    } finally {
      setSaving(false);
    }
  };

  if (!content) return <div className="admin-loading" data-testid="admin-content-loading">Loading content...</div>;

  return (
    <div className="admin-panel" data-testid="admin-panel">
      <header className="admin-header" data-testid="admin-header">
        <div className="admin-header-left">
          <span className="admin-brand-circle">circle</span>
          <span className="admin-brand-studios">Studios</span>
          <span className="admin-badge">CMS</span>
          <span
            className="admin-build-label"
            data-testid="admin-build-label"
            title="Deployed admin bundle version. If this doesn't match the latest commit, hard-refresh (Cmd/Ctrl+Shift+R) to load the newest bundle."
            style={{
              marginLeft: '10px',
              fontSize: '0.6rem',
              fontWeight: 600,
              letterSpacing: '0.04em',
              color: 'rgba(255,255,255,0.45)',
              padding: '2px 6px',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '3px',
              fontFamily: 'monospace',
            }}
          >
            {ADMIN_BUILD}
          </span>
        </div>
        <div className="admin-header-right">
          {saved && <span className="admin-saved-badge" data-testid="admin-saved-badge">Saved</span>}
          {error && <span className="admin-error-badge" data-testid="admin-error-badge">{error}</span>}
          <a href="/" target="_blank" rel="noopener noreferrer" className="admin-view-site" data-testid="admin-view-site">View Site</a>
          <button onClick={onLogout} className="admin-logout-btn" data-testid="admin-logout-btn">Sign Out</button>
        </div>
      </header>

      <nav className="admin-tabs" data-testid="admin-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`admin-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`admin-tab-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="admin-content">
        {activeTab === 'hero' && (
          <HeroEditor
            data={content.hero || {}}
            onChange={(d) => setContent((p) => ({ ...p, hero: d }))}
            onSave={() => saveSection('hero', content.hero)}
            token={token}
            saving={saving}
          />
        )}
        {activeTab === 'trustedPartners' && (
          <TrustedPartnersEditor
            data={content.trustedPartners || { logos: [] }}
            onChange={(d) => setContent((p) => ({ ...p, trustedPartners: d }))}
            onSave={() => saveSection('trustedPartners', content.trustedPartners)}
            token={token}
            saving={saving}
          />
        )}
        {activeTab === 'eventLogos' && (
          <EventLogosEditor
            data={content.eventLogos || { logos: [] }}
            onChange={(d) => setContent((p) => ({ ...p, eventLogos: d }))}
            onSave={() => saveSection('eventLogos', content.eventLogos)}
            token={token}
            saving={saving}
          />
        )}
        {activeTab === 'about' && (
          <AboutEditor
            data={content.about || {}}
            onChange={(d) => setContent((p) => ({ ...p, about: d }))}
            onSave={() => saveSection('about', content.about)}
            saving={saving}
          />
        )}
        {activeTab === 'capabilities' && (
          <CapabilitiesEditor
            data={content.capabilities || { items: [], stats: [] }}
            onChange={(d) => setContent((p) => ({ ...p, capabilities: d }))}
            onSave={() => saveSection('capabilities', content.capabilities)}
            token={token}
            saving={saving}
          />
        )}
        {activeTab === 'gallery' && (
          <GalleryManager token={token} />
        )}
        {activeTab === 'sustainability' && (
          <SustainabilityEditor
            data={content.sustainability || {}}
            onChange={(d) => setContent((p) => ({ ...p, sustainability: d }))}
            onSave={() => saveSection('sustainability', content.sustainability)}
            token={token}
            saving={saving}
          />
        )}
        {activeTab === 'proprietary' && (
          <ProprietaryEditor
            data={content.proprietary || { cards: [] }}
            onChange={(d) => setContent((p) => ({ ...p, proprietary: d }))}
            onSave={() => saveSection('proprietary', content.proprietary)}
            saving={saving}
          />
        )}
        {activeTab === 'contact' && (
          <ContactEditor
            data={content.contact || {}}
            onChange={(d) => setContent((p) => ({ ...p, contact: d }))}
            onSave={() => saveSection('contact', content.contact)}
            saving={saving}
          />
        )}
        {activeTab === 'footer' && (
          <FooterEditor
            data={content.footer || { navLinks: [] }}
            onChange={(d) => setContent((p) => ({ ...p, footer: d }))}
            onSave={() => saveSection('footer', content.footer)}
            saving={saving}
          />
        )}
        {activeTab === 'typography' && (
          <TypographyEditor
            data={content.typography || {}}
            content={content}
            onChange={(d) => setContent((p) => ({ ...p, typography: d }))}
            onSave={() => saveSection('typography', content.typography)}
            saving={saving}
          />
        )}
      </div>
    </div>
  );
}

// ─── Sub-editors ────────────────────────────────────────────────

function SaveButton({ saving, onSave }) {
  return (
    <button className="admin-save-btn" onClick={onSave} disabled={saving} data-testid="admin-save-btn">
      {saving ? 'Saving...' : 'Save Changes'}
    </button>
  );
}

function MediaUploadField({ label, mediaObj, onChange, token, fieldId }) {
  const [uploadState, setUploadState] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState('');
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = React.useRef(null);

  const value = mediaObj?.value || '';
  const isUpload = mediaObj?.type === 'upload';
  const API = process.env.REACT_APP_BACKEND_URL || '';

  const doUpload = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setUploadState('uploading');
    setProgress(0);
    setUploadError('');
    try {
      const result = await uploadFile(token, file, (percent) => setProgress(percent));
      setUploadState('success');
      setProgress(100);
      onChange({ type: 'upload', value: result.url });
      setTimeout(() => { setUploadState('idle'); setProgress(0); }, 4000);
    } catch (err) {
      setUploadState('error');
      setUploadError(err.message || 'Upload failed');
      setTimeout(() => { setUploadState('idle'); setProgress(0); setUploadError(''); }, 6000);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    doUpload(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) doUpload(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const handleRemove = () => { onChange(null); };

  const testId = fieldId || label?.toLowerCase().replace(/\s+/g, '-') || 'media';

  // Resolve preview URL
  const previewUrl = value && value.startsWith('/') ? `${API}${value}` : value;
  const isVideo = value && /\.(mp4|webm|ogg|mov)($|\?)/i.test(value);

  return (
    <div className="admin-field" data-testid={`upload-field-${testId}`}>
      <label className="admin-label">{label}</label>

      {/* Drag-and-drop upload zone */}
      <div
        className={`media-dropzone ${dragOver ? 'media-dropzone-active' : ''} ${uploadState === 'uploading' ? 'media-dropzone-uploading' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => uploadState !== 'uploading' && fileInputRef.current?.click()}
        data-testid={`dropzone-${testId}`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*,image/*"
          hidden
          onChange={handleFileSelect}
          disabled={uploadState === 'uploading'}
          data-testid={`file-input-${testId}`}
        />

        {uploadState === 'idle' && (
          <div className="media-dropzone-content">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" style={{marginBottom: '8px', opacity: 0.5}}>
              <path d="M14 4v16M8 10l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 20v3a2 2 0 002 2h18a2 2 0 002-2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="media-dropzone-text">Drop file here or click to browse</span>
            <span className="media-dropzone-hint">Video (MP4, WebM) or Image (JPG, PNG, WebP)</span>
          </div>
        )}

        {uploadState === 'uploading' && (
          <div className="media-dropzone-progress">
            <div className="media-progress-ring">
              <svg width="48" height="48" viewBox="0 0 48 48">
                <circle cx="24" cy="24" r="20" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3"/>
                <circle cx="24" cy="24" r="20" fill="none" stroke="#E87722" strokeWidth="3"
                  strokeDasharray={`${2 * Math.PI * 20}`}
                  strokeDashoffset={`${2 * Math.PI * 20 * (1 - progress / 100)}`}
                  strokeLinecap="round"
                  transform="rotate(-90 24 24)"
                  style={{transition: 'stroke-dashoffset 0.2s linear'}}
                />
              </svg>
              <span className="media-progress-pct">{progress}%</span>
            </div>
            <div className="media-progress-info">
              <span className="media-progress-filename">{fileName}</span>
              <span className="media-progress-status">Uploading...</span>
            </div>
          </div>
        )}

        {uploadState === 'success' && (
          <div className="media-dropzone-success">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span>Uploaded successfully{fileName ? ` — ${fileName}` : ''}</span>
          </div>
        )}

        {uploadState === 'error' && (
          <div className="media-dropzone-error">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><circle cx="10" cy="10" r="8" stroke="#EF4444" strokeWidth="1.5"/><path d="M10 6v5M10 13v.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round"/></svg>
            <span>{uploadError}</span>
            <span className="media-dropzone-hint" style={{marginTop:'4px'}}>Click to try again</span>
          </div>
        )}
      </div>

      {/* URL input (alternative) */}
      <div className="admin-media-url-row">
        <span className="admin-or">or paste URL:</span>
        <input
          type="text"
          className="admin-input"
          placeholder="https://..."
          value={value}
          onChange={(e) => onChange({ type: 'url', value: e.target.value })}
          data-testid={`url-input-${testId}`}
        />
      </div>

      {/* Preview + Remove */}
      {value && uploadState === 'idle' && (
        <div className="media-preview-area" data-testid={`preview-${testId}`}>
          {isVideo ? (
            <video src={previewUrl} controls muted className="media-preview-video" />
          ) : (
            <img src={previewUrl} alt="Preview" className="media-preview-img" />
          )}
          <div className="media-preview-actions">
            <span className="media-preview-tag">{isUpload ? 'Uploaded' : 'URL'}</span>
            <button className="media-remove-btn" onClick={(e) => { e.stopPropagation(); handleRemove(); }} data-testid={`remove-${testId}`}>Remove</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Hero Editor ────────────────────────────────────────────────

function HeroEditor({ data, onChange, onSave, token, saving }) {
  const update = (key, val) => onChange({ ...data, [key]: val });

  // Determine if the hero is using a video or static image
  const videoVal = data.video?.value || '';
  const posterVal = data.poster?.value || '';
  const isUsingVideo = videoVal && /\.(mp4|webm|ogg|mov)($|\?)/i.test(videoVal);
  const currentBgSource = isUsingVideo ? 'video' : 'image';
  const backgroundPreview = isUsingVideo ? videoVal : posterVal;

  return (
    <div className="admin-section" data-testid="admin-hero-editor">
      <h2 className="admin-section-title">Hero Section</h2>
      <p className="admin-hint">Controls the full-screen hero at the top of the homepage.</p>

      {/* Background Media */}
      <h3 className="admin-card-title" style={{ marginTop: '16px', marginBottom: '8px' }}>Background</h3>
      <p className="admin-hint">
        Currently using: <strong style={{ color: 'rgba(255,255,255,0.8)' }}>{currentBgSource === 'video' ? 'Looping Video' : 'Static Image'}</strong>
        {currentBgSource === 'image' && backgroundPreview && <> — to switch to video, upload an MP4 below</>}
      </p>

      <MediaUploadField
        label="Hero Background Image"
        fieldId="hero-bg-image"
        mediaObj={data.poster}
        onChange={(v) => update('poster', v)}
        token={token}
      />
      <p className="admin-hint" style={{ marginTop: '-8px' }}>The main background shown behind the hero text. Upload a high-res image (PNG/JPG, 1920px+ wide).</p>

      <MediaUploadField
        label="Background Video (optional — overrides image)"
        fieldId="hero-bg-video"
        mediaObj={data.video}
        onChange={(v) => update('video', v)}
        token={token}
      />
      <p className="admin-hint" style={{ marginTop: '-8px' }}>If set, a looping muted video replaces the static image. Upload MP4 (recommended under 50MB). Remove to use the image instead.</p>

      {/* Headline */}
      <h3 className="admin-card-title" style={{ marginTop: '32px', marginBottom: '8px' }}>Headline</h3>
      <p className="admin-hint">The large 3-line hero headline. Each line renders on its own row.</p>
      <div className="admin-field">
        <label className="admin-label">Line 1</label>
        <input className="admin-input" value={data.headlineLine1 || ''} onChange={(e) => update('headlineLine1', e.target.value)} placeholder="Where" data-testid="hero-headline1" />
      </div>
      <div className="admin-field">
        <label className="admin-label">Line 2</label>
        <input className="admin-input" value={data.headlineLine2 || ''} onChange={(e) => update('headlineLine2', e.target.value)} placeholder="Experiences" data-testid="hero-headline2" />
      </div>
      <div className="admin-field">
        <label className="admin-label">Line 3</label>
        <input className="admin-input" value={data.headlineLine3 || ''} onChange={(e) => update('headlineLine3', e.target.value)} placeholder="Become Influence" data-testid="hero-headline3" />
      </div>

      {/* Body Copy */}
      <h3 className="admin-card-title" style={{ marginTop: '32px', marginBottom: '8px' }}>Body Copy</h3>
      <div className="admin-field">
        <label className="admin-label">Subheadline Paragraph</label>
        <textarea className="admin-textarea" value={data.subheadline || ''} onChange={(e) => update('subheadline', e.target.value)} data-testid="hero-subheadline" />
      </div>

      {/* CTA Buttons */}
      <h3 className="admin-card-title" style={{ marginTop: '32px', marginBottom: '8px' }}>Buttons</h3>
      <p className="admin-hint">The two call-to-action buttons below the body copy. Links starting with # scroll to a section on the page (e.g. #gallery for Our Work, #contact, #capabilities).</p>
      <div className="admin-card" style={{ padding: '16px' }}>
        <h4 style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Button 1 (Filled)</h4>
        <div className="admin-row">
          <div className="admin-field">
            <label className="admin-label">Button Text</label>
            <input className="admin-input" value={data.ctaPrimaryText || ''} onChange={(e) => update('ctaPrimaryText', e.target.value)} placeholder="Gallery" data-testid="hero-cta-primary" />
          </div>
          <div className="admin-field">
            <label className="admin-label">Button Link</label>
            <input className="admin-input" value={data.ctaPrimaryLink || ''} onChange={(e) => update('ctaPrimaryLink', e.target.value)} placeholder="#gallery" data-testid="hero-cta-primary-link" />
          </div>
        </div>
      </div>
      <div className="admin-card" style={{ padding: '16px', marginTop: '8px' }}>
        <h4 style={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Button 2 (Outline)</h4>
        <div className="admin-row">
          <div className="admin-field">
            <label className="admin-label">Button Text</label>
            <input className="admin-input" value={data.ctaSecondaryText || ''} onChange={(e) => update('ctaSecondaryText', e.target.value)} placeholder="Connect With Us" data-testid="hero-cta-secondary" />
          </div>
          <div className="admin-field">
            <label className="admin-label">Button Link</label>
            <input className="admin-input" value={data.ctaSecondaryLink || ''} onChange={(e) => update('ctaSecondaryLink', e.target.value)} placeholder="#contact" data-testid="hero-cta-secondary-link" />
          </div>
        </div>
      </div>

      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── Trusted Partners Editor ────────────────────────────────────

function TrustedPartnersEditor({ data, onChange, onSave, token, saving }) {
  const logos = data.logos || [];
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const [uploadState, setUploadState] = useState('idle');
  const [progress, setProgress] = useState(0);
  const fileInputRef = React.useRef(null);

  const updateLogos = (newLogos) => onChange({ ...data, logos: newLogos });

  const updateLogo = (idx, key, val) => {
    const updated = [...logos];
    updated[idx] = { ...updated[idx], [key]: val };
    updateLogos(updated);
  };

  const removeLogo = (idx) => updateLogos(logos.filter((_, i) => i !== idx));

  const moveLogo = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= logos.length) return;
    const updated = [...logos];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updateLogos(updated);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploadState('uploading');
    setProgress(0);
    try {
      const result = await uploadFile(token, file, (pct) => setProgress(pct));
      setUploadState('idle');
      setProgress(0);
      const name = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      updateLogos([...logos, { src: result.url, alt: name }]);
    } catch (err) {
      setUploadState('idle');
      setProgress(0);
      alert('Upload failed: ' + (err.message || 'Unknown error'));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length === 1) {
      handleUpload(files[0]);
    } else {
      // Bulk upload
      (async () => {
        for (const file of files) {
          await handleUpload(file);
        }
      })();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length === 1) {
      handleUpload(files[0]);
    } else if (files.length > 1) {
      (async () => { for (const f of files) await handleUpload(f); })();
    }
  };

  const addFromUrl = () => {
    const url = prompt('Enter logo image URL:');
    if (url && url.trim()) {
      const alt = prompt('Enter brand/partner name:', '') || '';
      updateLogos([...logos, { src: url.trim(), alt: alt.trim() }]);
    }
  };

  const resolveLogoUrl = (src) => src && src.startsWith('/') ? `${API}${src}` : src;

  return (
    <div className="admin-section" data-testid="admin-trusted-partners-editor">
      <h2 className="admin-section-title">Trusted Partners ({logos.length} logos)</h2>
      <p className="admin-hint">Manage the scrolling logo bar that appears below the hero section. Logos display as white images on a dark background.</p>

      {/* Upload zone */}
      <div
        className={`media-dropzone ${uploadState === 'uploading' ? 'media-dropzone-uploading' : ''}`}
        onClick={() => uploadState !== 'uploading' && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={handleDrop}
        data-testid="trusted-upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFileSelect}
          data-testid="trusted-file-input"
        />
        {uploadState === 'uploading' ? (
          <div className="media-dropzone-progress">
            <span>Uploading... {progress}%</span>
          </div>
        ) : (
          <div className="media-dropzone-content">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" style={{marginBottom: '8px', opacity: 0.5}}>
              <path d="M14 4v16M8 10l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 20v3a2 2 0 002 2h18a2 2 0 002-2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="media-dropzone-text">Drop logo images here or click to browse</span>
            <span className="media-dropzone-hint">PNG recommended (white logos on transparent background). Bulk upload supported.</span>
          </div>
        )}
      </div>

      <button className="admin-add-btn" onClick={addFromUrl} style={{ marginTop: '8px', marginBottom: '16px' }} data-testid="trusted-add-url">+ Add Logo from URL</button>

      {/* Logo grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }} data-testid="trusted-logos-grid">
        {logos.map((logo, idx) => (
          <div key={idx} className="admin-card" style={{ padding: '12px' }} data-testid={`trusted-logo-card-${idx}`}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60px', background: 'rgba(0,0,0,0.4)', borderRadius: '6px', marginBottom: '8px', padding: '8px' }}>
              <img src={resolveLogoUrl(logo.src)} alt={logo.alt} style={{ maxHeight: '44px', maxWidth: '100%', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            </div>
            <div className="admin-field" style={{ marginBottom: '4px' }}>
              <input className="admin-input" value={logo.alt || ''} onChange={(e) => updateLogo(idx, 'alt', e.target.value)} placeholder="Brand name" style={{ fontSize: '0.75rem', padding: '4px 8px' }} data-testid={`trusted-alt-${idx}`} />
            </div>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button className="admin-remove-btn" onClick={() => moveLogo(idx, -1)} disabled={idx === 0} style={{ opacity: idx === 0 ? 0.3 : 1, fontSize: '0.65rem', padding: '2px 6px' }} title="Move left" data-testid={`trusted-move-up-${idx}`}>&laquo;</button>
                <button className="admin-remove-btn" onClick={() => moveLogo(idx, 1)} disabled={idx === logos.length - 1} style={{ opacity: idx === logos.length - 1 ? 0.3 : 1, fontSize: '0.65rem', padding: '2px 6px' }} title="Move right" data-testid={`trusted-move-down-${idx}`}>&raquo;</button>
              </div>
              <span style={{ fontSize: '0.65rem', opacity: 0.4 }}>#{idx + 1}</span>
              <button className="admin-remove-btn" onClick={() => removeLogo(idx)} title="Delete" data-testid={`trusted-delete-${idx}`}>x</button>
            </div>
          </div>
        ))}
      </div>

      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── About Editor ───────────────────────────────────────────────

function EventLogosEditor({ data, onChange, onSave, token, saving }) {
  const logos = data.logos || [];
  const API = process.env.REACT_APP_BACKEND_URL || '';
  const [uploadState, setUploadState] = useState('idle');
  const [progress, setProgress] = useState(0);
  const fileInputRef = React.useRef(null);

  const updateLogos = (newLogos) => onChange({ ...data, logos: newLogos });

  const updateLogo = (idx, key, val) => {
    const updated = [...logos];
    updated[idx] = { ...updated[idx], [key]: val };
    updateLogos(updated);
  };

  const removeLogo = (idx) => updateLogos(logos.filter((_, i) => i !== idx));

  const moveLogo = (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= logos.length) return;
    const updated = [...logos];
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    updateLogos(updated);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    setUploadState('uploading');
    setProgress(0);
    try {
      const result = await uploadFile(token, file, (pct) => setProgress(pct));
      setUploadState('idle');
      setProgress(0);
      const name = file.name.replace(/\.[^/.]+$/, '').replace(/[_-]/g, ' ');
      updateLogos([...logos, { src: result.url, alt: name }]);
    } catch (err) {
      setUploadState('idle');
      setProgress(0);
      alert('Upload failed: ' + (err.message || 'Unknown error'));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = '';
    if (files.length === 1) {
      handleUpload(files[0]);
    } else {
      (async () => { for (const file of files) await handleUpload(file); })();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    if (files.length === 1) {
      handleUpload(files[0]);
    } else if (files.length > 1) {
      (async () => { for (const f of files) await handleUpload(f); })();
    }
  };

  const addFromUrl = () => {
    const url = prompt('Enter event/location logo image URL:');
    if (url && url.trim()) {
      const alt = prompt('Enter event/location name:', '') || '';
      updateLogos([...logos, { src: url.trim(), alt: alt.trim() }]);
    }
  };

  const resolveLogoUrl = (src) => src && src.startsWith('/') ? `${API}${src}` : src;

  return (
    <div className="admin-section" data-testid="admin-event-logos-editor">
      <h2 className="admin-section-title">Event / Location Logos ({logos.length} logos)</h2>
      <p className="admin-hint">Manage the second scrolling logo bar (scrolls opposite direction to brand logos). These are location and event logos shown beneath the brand partner scroll.</p>

      <div
        className={`media-dropzone ${uploadState === 'uploading' ? 'media-dropzone-uploading' : ''}`}
        onClick={() => uploadState !== 'uploading' && fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={handleDrop}
        data-testid="event-upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleFileSelect}
          data-testid="event-file-input"
        />
        {uploadState === 'uploading' ? (
          <div className="media-dropzone-progress">
            <span>Uploading... {progress}%</span>
          </div>
        ) : (
          <div className="media-dropzone-content">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" style={{marginBottom: '8px', opacity: 0.5}}>
              <path d="M14 4v16M8 10l6-6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 20v3a2 2 0 002 2h18a2 2 0 002-2v-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="media-dropzone-text">Drop event/location logo images here or click to browse</span>
            <span className="media-dropzone-hint">PNG recommended (white logos on transparent background). Bulk upload supported.</span>
          </div>
        )}
      </div>

      <button className="admin-add-btn" onClick={addFromUrl} style={{ marginTop: '8px', marginBottom: '16px' }} data-testid="event-add-url">+ Add Logo from URL</button>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }} data-testid="event-logos-grid">
        {logos.map((logo, idx) => (
          <div key={idx} className="admin-card" style={{ padding: '12px' }} data-testid={`event-logo-card-${idx}`}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60px', background: 'rgba(0,0,0,0.4)', borderRadius: '6px', marginBottom: '8px', padding: '8px' }}>
              <img src={resolveLogoUrl(logo.src)} alt={logo.alt} style={{ maxHeight: '44px', maxWidth: '100%', objectFit: 'contain', filter: 'brightness(0) invert(1)' }} />
            </div>
            <div className="admin-field" style={{ marginBottom: '4px' }}>
              <input className="admin-input" value={logo.alt || ''} onChange={(e) => updateLogo(idx, 'alt', e.target.value)} placeholder="Event/Location name" style={{ fontSize: '0.75rem', padding: '4px 8px' }} data-testid={`event-alt-${idx}`} />
            </div>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button className="admin-remove-btn" onClick={() => moveLogo(idx, -1)} disabled={idx === 0} style={{ opacity: idx === 0 ? 0.3 : 1, fontSize: '0.65rem', padding: '2px 6px' }} title="Move left" data-testid={`event-move-up-${idx}`}>&laquo;</button>
                <button className="admin-remove-btn" onClick={() => moveLogo(idx, 1)} disabled={idx === logos.length - 1} style={{ opacity: idx === logos.length - 1 ? 0.3 : 1, fontSize: '0.65rem', padding: '2px 6px' }} title="Move right" data-testid={`event-move-down-${idx}`}>&raquo;</button>
              </div>
              <span style={{ fontSize: '0.65rem', opacity: 0.4 }}>#{idx + 1}</span>
              <button className="admin-remove-btn" onClick={() => removeLogo(idx)} title="Delete" data-testid={`event-delete-${idx}`}>x</button>
            </div>
          </div>
        ))}
      </div>

      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── About Editor (original) ────────────────────────────────────

function AboutEditor({ data, onChange, onSave, saving }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  const updatePhilosophy = (idx, val) => {
    const items = [...(data.philosophy || [])];
    items[idx] = val;
    update('philosophy', items);
  };
  const addPhilosophy = () => update('philosophy', [...(data.philosophy || []), '']);
  const removePhilosophy = (idx) => update('philosophy', (data.philosophy || []).filter((_, i) => i !== idx));

  return (
    <div className="admin-section" data-testid="admin-about-editor">
      <h2 className="admin-section-title">About</h2>
      <div className="admin-field">
        <label className="admin-label">Title</label>
        <input className="admin-input" value={data.title || ''} onChange={(e) => update('title', e.target.value)} data-testid="about-title-input" />
      </div>
      <div className="admin-field">
        <label className="admin-label">Main Text</label>
        <textarea className="admin-textarea" value={data.text || ''} onChange={(e) => update('text', e.target.value)} data-testid="about-text-input" />
      </div>
      <h3 className="admin-card-title" style={{marginTop: '16px'}}>Philosophy Lines</h3>
      {(data.philosophy || []).map((line, idx) => (
        <div key={idx} className="admin-list-item" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input className="admin-input" style={{ flex: 1 }} value={line} onChange={(e) => updatePhilosophy(idx, e.target.value)} data-testid={`philosophy-input-${idx}`} />
          <button className="admin-remove-btn" onClick={() => removePhilosophy(idx)} data-testid={`philosophy-remove-${idx}`}>x</button>
        </div>
      ))}
      <button className="admin-add-btn" onClick={addPhilosophy} data-testid="about-add-philosophy">+ Add Philosophy Line</button>
      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── Capabilities Editor ────────────────────────────────────────

function CapabilitiesEditor({ data, onChange, onSave, token, saving }) {
  const update = (key, val) => onChange({ ...data, [key]: val });

  // --- Items (neon glow cards with title + description) ---
  const updateItem = (idx, key, val) => {
    const items = [...(data.items || [])];
    items[idx] = { ...items[idx], [key]: val };
    update('items', items);
  };
  const addItem = () => update('items', [...(data.items || []), { title: '', text: '' }]);
  const removeItem = (idx) => update('items', (data.items || []).filter((_, i) => i !== idx));
  const moveItem = (idx, dir) => {
    const items = [...(data.items || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    update('items', items);
  };

  // --- Stats ---
  const stats = data.stats || [];
  const updateStat = (idx, key, val) => {
    const s = [...stats];
    s[idx] = { ...s[idx], [key]: key === 'num' ? (parseInt(val, 10) || 0) : val };
    update('stats', s);
  };
  const addStat = () => update('stats', [...stats, { num: 0, suffix: '+', label: '' }]);
  const removeStat = (idx) => update('stats', stats.filter((_, i) => i !== idx));

  return (
    <div className="admin-section" data-testid="admin-capabilities-editor">
      <h2 className="admin-section-title">Capabilities</h2>
      <p className="admin-hint">Controls the Capabilities card grid and the Impact Transition section (video + animated stats) above it.</p>

      <h3 className="admin-card-title" style={{ marginTop: '16px', marginBottom: '8px' }}>Impact Video</h3>
      <p className="admin-hint">The video player that appears between the About section and the Capabilities cards.</p>
      <MediaUploadField
        label="Impact Section Video"
        fieldId="capabilities-video"
        mediaObj={data.video}
        onChange={(v) => update('video', v)}
        token={token}
      />

      <div className="admin-field" style={{ marginTop: '24px' }}>
        <label className="admin-label">Section Title</label>
        <input className="admin-input" value={data.title || ''} onChange={(e) => update('title', e.target.value)} placeholder="Capabilities" data-testid="cap-title" />
      </div>
      <div className="admin-field">
        <label className="admin-label">Subtitle</label>
        <input className="admin-input" value={data.subtitle || ''} onChange={(e) => update('subtitle', e.target.value)} placeholder="End-to-End Execution Across the Experience Lifecycle" data-testid="cap-subtitle" />
      </div>

      <h3 className="admin-card-title" style={{ marginTop: '32px', marginBottom: '8px' }}>Capability Cards</h3>
      <p className="admin-hint">Each card displays a title and a description paragraph in a neon-bordered card grid.</p>
      {(data.items || []).map((item, idx) => (
        <div key={idx} className="admin-card" data-testid={`cap-card-${idx}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 className="admin-card-title" style={{ margin: 0 }}>Card {idx + 1}</h3>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button className="admin-remove-btn" onClick={() => moveItem(idx, -1)} disabled={idx === 0} style={{ opacity: idx === 0 ? 0.3 : 1, fontSize: '0.7rem', padding: '2px 8px' }} data-testid={`cap-move-up-${idx}`}>&#9650;</button>
              <button className="admin-remove-btn" onClick={() => moveItem(idx, 1)} disabled={idx === (data.items || []).length - 1} style={{ opacity: idx === (data.items || []).length - 1 ? 0.3 : 1, fontSize: '0.7rem', padding: '2px 8px' }} data-testid={`cap-move-down-${idx}`}>&#9660;</button>
              <button className="admin-remove-btn" onClick={() => removeItem(idx)} data-testid={`cap-delete-${idx}`}>x</button>
            </div>
          </div>
          <div className="admin-field">
            <label className="admin-label">Title</label>
            <input className="admin-input" value={item.title || ''} onChange={(e) => updateItem(idx, 'title', e.target.value)} data-testid={`cap-item-title-${idx}`} />
          </div>
          <div className="admin-field">
            <label className="admin-label">Description</label>
            <textarea className="admin-textarea" value={item.text || ''} onChange={(e) => updateItem(idx, 'text', e.target.value)} style={{ minHeight: '60px' }} data-testid={`cap-item-text-${idx}`} />
          </div>
        </div>
      ))}
      <button className="admin-add-btn" onClick={addItem} data-testid="cap-add-item">+ Add Capability</button>

      <h3 className="admin-card-title" style={{ marginTop: '32px', marginBottom: '12px' }}>Impact Stats</h3>
      <p className="admin-hint">The animated number counters displayed below the video, above the Capabilities cards.</p>
      {stats.map((stat, idx) => (
        <div key={idx} className="admin-card" style={{ padding: '12px' }} data-testid={`cap-stat-${idx}`}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <div className="admin-field" style={{ flex: '0 0 80px' }}>
              <label className="admin-label">Number</label>
              <input className="admin-input" type="number" value={stat.num || 0} onChange={(e) => updateStat(idx, 'num', e.target.value)} data-testid={`cap-stat-num-${idx}`} />
            </div>
            <div className="admin-field" style={{ flex: '0 0 60px' }}>
              <label className="admin-label">Suffix</label>
              <input className="admin-input" value={stat.suffix || ''} onChange={(e) => updateStat(idx, 'suffix', e.target.value)} placeholder="+" data-testid={`cap-stat-suffix-${idx}`} />
            </div>
            <div className="admin-field" style={{ flex: 1 }}>
              <label className="admin-label">Label</label>
              <input className="admin-input" value={stat.label || ''} onChange={(e) => updateStat(idx, 'label', e.target.value)} data-testid={`cap-stat-label-${idx}`} />
            </div>
            <button className="admin-remove-btn" onClick={() => removeStat(idx)} style={{ marginBottom: '2px' }} data-testid={`cap-stat-remove-${idx}`}>x</button>
          </div>
        </div>
      ))}
      <button className="admin-add-btn" onClick={addStat} data-testid="cap-add-stat">+ Add Stat</button>

      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── Sustainability Editor ──────────────────────────────────────

function SustainabilityEditor({ data, onChange, onSave, token, saving }) {
  const update = (key, val) => onChange({ ...data, [key]: val });

  // Impact items
  const impactItems = data.impactItems || [];
  const updateImpact = (idx, key, val) => {
    const items = [...impactItems];
    items[idx] = { ...items[idx], [key]: val };
    update('impactItems', items);
  };
  const addImpact = () => update('impactItems', [...impactItems, { icon: String(impactItems.length + 1).padStart(2, '0'), label: '', stat: '', desc: '' }]);
  const removeImpact = (idx) => update('impactItems', impactItems.filter((_, i) => i !== idx));

  // Platforms
  const platforms = data.platforms || [];
  const updatePlatform = (idx, key, val) => {
    const items = [...platforms];
    items[idx] = { ...items[idx], [key]: val };
    update('platforms', items);
  };
  const updatePlatformBullet = (pIdx, bIdx, val) => {
    const items = [...platforms];
    const bullets = [...(items[pIdx].bullets || [])];
    bullets[bIdx] = val;
    items[pIdx] = { ...items[pIdx], bullets };
    update('platforms', items);
  };
  const addPlatformBullet = (pIdx) => {
    const items = [...platforms];
    items[pIdx] = { ...items[pIdx], bullets: [...(items[pIdx].bullets || []), ''] };
    update('platforms', items);
  };
  const removePlatformBullet = (pIdx, bIdx) => {
    const items = [...platforms];
    items[pIdx] = { ...items[pIdx], bullets: (items[pIdx].bullets || []).filter((_, i) => i !== bIdx) };
    update('platforms', items);
  };
  const addPlatform = () => update('platforms', [...platforms, { id: `platform-${Date.now()}`, name: '', tagline: '', bullets: [''], accent: 'blue' }]);
  const removePlatform = (idx) => update('platforms', platforms.filter((_, i) => i !== idx));

  return (
    <div className="admin-section" data-testid="admin-sustainability-editor">
      <h2 className="admin-section-title">Sustainability</h2>

      <div className="admin-field">
        <label className="admin-label">Section Title</label>
        <input className="admin-input" value={data.title || ''} onChange={(e) => update('title', e.target.value)} data-testid="sus-title" />
      </div>
      <div className="admin-field">
        <label className="admin-label">Intro Paragraph</label>
        <textarea className="admin-textarea" value={data.intro || ''} onChange={(e) => update('intro', e.target.value)} data-testid="sus-intro" />
      </div>

      {/* Videos */}
      <h3 className="admin-card-title" style={{ marginTop: '24px', marginBottom: '12px' }}>Platform Videos</h3>
      <MediaUploadField
        label="STIX Platform Card Video"
        fieldId="sus-stix-platform-video"
        mediaObj={data.stixPlatformVideo}
        onChange={(v) => update('stixPlatformVideo', v)}
        token={token}
      />
      <p className="admin-hint">Looping video background for the STIX Modular Exhibit System card. Recommended: MP4, 720p+, under 50MB.</p>
      <MediaUploadField
        label="ReTread Platform Card Video"
        fieldId="sus-retread-platform-video"
        mediaObj={data.retreadPlatformVideo}
        onChange={(v) => update('retreadPlatformVideo', v)}
        token={token}
      />
      <p className="admin-hint">Looping video background for the ReTread Surfaces card. Recommended: MP4, 720p+, under 50MB.</p>

      {/* Impact Items */}
      <h3 className="admin-card-title" style={{ marginTop: '32px', marginBottom: '12px' }}>Impact Metrics</h3>
      <p className="admin-hint">The numbered impact cards shown in the sustainability hero area.</p>
      {impactItems.map((item, idx) => (
        <div key={idx} className="admin-card" style={{ padding: '12px' }} data-testid={`sus-impact-${idx}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <span style={{ fontWeight: 600, fontSize: '0.8rem', opacity: 0.7 }}>Impact {idx + 1}</span>
            <button className="admin-remove-btn" onClick={() => removeImpact(idx)} data-testid={`sus-impact-remove-${idx}`}>x</button>
          </div>
          <div className="admin-row">
            <div className="admin-field" style={{ flex: '0 0 60px' }}>
              <label className="admin-label">Icon #</label>
              <input className="admin-input" value={item.icon || ''} onChange={(e) => updateImpact(idx, 'icon', e.target.value)} data-testid={`sus-impact-icon-${idx}`} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Label</label>
              <input className="admin-input" value={item.label || ''} onChange={(e) => updateImpact(idx, 'label', e.target.value)} data-testid={`sus-impact-label-${idx}`} />
            </div>
            <div className="admin-field" style={{ flex: '0 0 100px' }}>
              <label className="admin-label">Stat</label>
              <input className="admin-input" value={item.stat || ''} onChange={(e) => updateImpact(idx, 'stat', e.target.value)} data-testid={`sus-impact-stat-${idx}`} />
            </div>
          </div>
          <div className="admin-field">
            <label className="admin-label">Description</label>
            <input className="admin-input" value={item.desc || ''} onChange={(e) => updateImpact(idx, 'desc', e.target.value)} data-testid={`sus-impact-desc-${idx}`} />
          </div>
        </div>
      ))}
      <button className="admin-add-btn" onClick={addImpact} data-testid="sus-add-impact">+ Add Impact Metric</button>

      {/* Platforms */}
      <h3 className="admin-card-title" style={{ marginTop: '32px', marginBottom: '12px' }}>System Platforms</h3>
      <p className="admin-hint">The platform cards (STIX, ReTread) with details and bullet points.</p>
      {platforms.map((plat, idx) => (
        <div key={idx} className="admin-card" data-testid={`sus-platform-${idx}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 className="admin-card-title" style={{ margin: 0 }}>Platform {idx + 1}</h3>
            <button className="admin-remove-btn" onClick={() => removePlatform(idx)} data-testid={`sus-platform-remove-${idx}`}>x</button>
          </div>
          <div className="admin-row">
            <div className="admin-field" style={{ flex: '0 0 80px' }}>
              <label className="admin-label">ID</label>
              <input className="admin-input" value={plat.id || ''} onChange={(e) => updatePlatform(idx, 'id', e.target.value)} data-testid={`sus-plat-id-${idx}`} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Name</label>
              <input className="admin-input" value={plat.name || ''} onChange={(e) => updatePlatform(idx, 'name', e.target.value)} data-testid={`sus-plat-name-${idx}`} />
            </div>
            <div className="admin-field" style={{ flex: '0 0 80px' }}>
              <label className="admin-label">Accent</label>
              <select className="admin-input" value={plat.accent || 'blue'} onChange={(e) => updatePlatform(idx, 'accent', e.target.value)} data-testid={`sus-plat-accent-${idx}`}>
                <option value="blue">Blue</option>
                <option value="violet">Violet</option>
                <option value="green">Green</option>
                <option value="orange">Orange</option>
              </select>
            </div>
          </div>
          <div className="admin-field">
            <label className="admin-label">Tagline</label>
            <input className="admin-input" value={plat.tagline || ''} onChange={(e) => updatePlatform(idx, 'tagline', e.target.value)} data-testid={`sus-plat-tagline-${idx}`} />
          </div>
          <div className="admin-field">
            <label className="admin-label">Bullet Points</label>
            {(plat.bullets || []).map((b, bIdx) => (
              <div key={bIdx} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' }}>
                <span style={{ opacity: 0.4, fontSize: '0.7rem', minWidth: '16px' }}>{bIdx + 1}.</span>
                <input className="admin-input" style={{ flex: 1 }} value={b} onChange={(e) => updatePlatformBullet(idx, bIdx, e.target.value)} data-testid={`sus-plat-bullet-${idx}-${bIdx}`} />
                <button className="admin-remove-btn" onClick={() => removePlatformBullet(idx, bIdx)} style={{ fontSize: '0.65rem', padding: '2px 6px' }} data-testid={`sus-plat-bullet-remove-${idx}-${bIdx}`}>x</button>
              </div>
            ))}
            <button className="admin-add-btn" onClick={() => addPlatformBullet(idx)} style={{ fontSize: '0.7rem', padding: '4px 10px' }} data-testid={`sus-plat-add-bullet-${idx}`}>+ Add Bullet</button>
          </div>
        </div>
      ))}
      <button className="admin-add-btn" onClick={addPlatform} data-testid="sus-add-platform">+ Add Platform</button>

      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── Simple Editor ──────────────────────────────────────────────

function SimpleEditor({ title, fields, data, onChange, onSave, saving }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  return (
    <div className="admin-section" data-testid={`admin-${title.toLowerCase()}-editor`}>
      <h2 className="admin-section-title">{title}</h2>
      {fields.map((f) => (
        <div className="admin-field" key={f.key}>
          <label className="admin-label">{f.label}</label>
          {f.type === 'textarea' ? (
            <textarea className="admin-textarea" value={data[f.key] || ''} onChange={(e) => update(f.key, e.target.value)} data-testid={`${title.toLowerCase()}-${f.key}`} />
          ) : (
            <input className="admin-input" value={data[f.key] || ''} onChange={(e) => update(f.key, e.target.value)} data-testid={`${title.toLowerCase()}-${f.key}`} />
          )}
        </div>
      ))}
      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── Contact Editor ─────────────────────────────────────────

function ContactEditor({ data, onChange, onSave, saving }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  return (
    <div className="admin-section" data-testid="admin-contact-editor">
      <h2 className="admin-section-title">Contact</h2>
      <p className="admin-hint">Controls the "Start a Conversation" section near the bottom of the page.</p>

      <div className="admin-field">
        <label className="admin-label">Eyebrow Text</label>
        <input className="admin-input" value={data.eyebrow || ''} onChange={(e) => update('eyebrow', e.target.value)} placeholder="Start a Conversation" data-testid="contact-eyebrow-input" />
        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', marginTop: '4px', display: 'block' }}>Small uppercase text above the headline</span>
      </div>
      <div className="admin-field">
        <label className="admin-label">Headline</label>
        <textarea className="admin-textarea" value={data.title || ''} onChange={(e) => update('title', e.target.value)} placeholder={'Ready to build\nsomething\ninfluential?'} data-testid="contact-title" />
        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', marginTop: '4px', display: 'block' }}>Use line breaks (Enter) to control where text wraps on the live site</span>
      </div>

      <h3 className="admin-card-title" style={{ marginTop: '24px', marginBottom: '8px' }}>Button</h3>
      <div className="admin-row">
        <div className="admin-field">
          <label className="admin-label">Button Text</label>
          <input className="admin-input" value={data.ctaText || ''} onChange={(e) => update('ctaText', e.target.value)} placeholder="Begin Inquiry" data-testid="contact-ctaText" />
        </div>
        <div className="admin-field">
          <label className="admin-label">Button Link</label>
          <input className="admin-input" value={data.ctaLink || ''} onChange={(e) => update('ctaLink', e.target.value)} placeholder="mailto:info@circlestudios.com" data-testid="contact-ctaLink" />
          <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', marginTop: '4px', display: 'block' }}>Use mailto: for email, https:// for external links, or #section for on-page scroll</span>
        </div>
      </div>

      <div className="admin-field" style={{ marginTop: '16px' }}>
        <label className="admin-label">Subtitle / Supporting Text</label>
        <textarea className="admin-textarea" value={data.subtitle || ''} onChange={(e) => update('subtitle', e.target.value)} data-testid="contact-subtitle-input" />
        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', marginTop: '4px', display: 'block' }}>Appears below the button in smaller text</span>
      </div>

      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── Proprietary Editor ─────────────────────────────────────────

function ProprietaryEditor({ data, onChange, onSave, saving }) {
  const cards = data.cards || [
    { name: 'SEICON', href: '#', color: '#E8443A', accent: '#FF6B5E', visible: true },
    { name: 'FANPRIX', href: '#', color: '#7B5CF5', accent: '#A78BFA', visible: true },
    { name: 'OZFEST', href: '#', color: '#10B981', accent: '#34D399', visible: true },
  ];
  const sectionLabel = data.sectionLabel || 'Explore';
  const sectionTitle = data.sectionTitle || 'Proprietary Engagements';

  const update = (key, val) => onChange({ ...data, cards, sectionLabel, sectionTitle, [key]: val });
  const updateCard = (idx, key, val) => {
    const updated = [...cards];
    updated[idx] = { ...updated[idx], [key]: val };
    update('cards', updated);
  };
  const addCard = () => {
    update('cards', [...cards, { name: '', href: '#', color: '#ffffff', accent: '#cccccc', visible: true }]);
  };
  const removeCard = (idx) => {
    update('cards', cards.filter((_, i) => i !== idx));
  };
  const moveCard = (idx, dir) => {
    const updated = [...cards];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= updated.length) return;
    [updated[idx], updated[newIdx]] = [updated[newIdx], updated[idx]];
    update('cards', updated);
  };

  return (
    <div className="admin-section" data-testid="admin-proprietary-editor">
      <h2 className="admin-section-title">Proprietary Engagements</h2>

      <div className="admin-row">
        <div className="admin-field">
          <label className="admin-label">Section Label</label>
          <input className="admin-input" value={sectionLabel} onChange={(e) => update('sectionLabel', e.target.value)} data-testid="prop-section-label" />
        </div>
        <div className="admin-field">
          <label className="admin-label">Section Title</label>
          <input className="admin-input" value={sectionTitle} onChange={(e) => update('sectionTitle', e.target.value)} data-testid="prop-section-title" />
        </div>
      </div>

      <h3 className="admin-card-title" style={{ marginTop: '24px', marginBottom: '12px' }}>Engagement Cards</h3>

      {cards.map((card, idx) => (
        <div key={idx} className="admin-card" data-testid={`prop-card-editor-${idx}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 className="admin-card-title" style={{ margin: 0 }}>Card {idx + 1}</h3>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <button className="admin-remove-btn" onClick={() => moveCard(idx, -1)} disabled={idx === 0} style={{ opacity: idx === 0 ? 0.3 : 1, fontSize: '0.7rem', padding: '2px 8px' }} title="Move up" data-testid={`prop-move-up-${idx}`}>&#9650;</button>
              <button className="admin-remove-btn" onClick={() => moveCard(idx, 1)} disabled={idx === cards.length - 1} style={{ opacity: idx === cards.length - 1 ? 0.3 : 1, fontSize: '0.7rem', padding: '2px 8px' }} title="Move down" data-testid={`prop-move-down-${idx}`}>&#9660;</button>
              <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>
                <input type="checkbox" checked={card.visible !== false} onChange={(e) => updateCard(idx, 'visible', e.target.checked)} data-testid={`prop-visible-${idx}`} />
                Visible
              </label>
              <button className="admin-remove-btn" onClick={() => removeCard(idx)} title="Delete card" data-testid={`prop-delete-${idx}`}>x</button>
            </div>
          </div>
          <div className="admin-row">
            <div className="admin-field">
              <label className="admin-label">Name</label>
              <input className="admin-input" value={card.name || ''} onChange={(e) => updateCard(idx, 'name', e.target.value)} data-testid={`prop-name-${idx}`} />
            </div>
            <div className="admin-field">
              <label className="admin-label">Link URL</label>
              <input className="admin-input" value={card.href || ''} onChange={(e) => updateCard(idx, 'href', e.target.value)} placeholder="https://..." data-testid={`prop-href-${idx}`} />
            </div>
          </div>
          <div className="admin-row">
            <div className="admin-field">
              <label className="admin-label">Primary Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={card.color || '#ffffff'} onChange={(e) => updateCard(idx, 'color', e.target.value)} style={{ width: '36px', height: '28px', cursor: 'pointer', border: 'none', background: 'none' }} data-testid={`prop-color-${idx}`} />
                <input className="admin-input" value={card.color || ''} onChange={(e) => updateCard(idx, 'color', e.target.value)} style={{ flex: 1 }} />
              </div>
            </div>
            <div className="admin-field">
              <label className="admin-label">Accent Color</label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input type="color" value={card.accent || '#cccccc'} onChange={(e) => updateCard(idx, 'accent', e.target.value)} style={{ width: '36px', height: '28px', cursor: 'pointer', border: 'none', background: 'none' }} data-testid={`prop-accent-${idx}`} />
                <input className="admin-input" value={card.accent || ''} onChange={(e) => updateCard(idx, 'accent', e.target.value)} style={{ flex: 1 }} />
              </div>
            </div>
          </div>
        </div>
      ))}

      <button className="admin-add-btn" onClick={addCard} data-testid="prop-add-card">+ Add Engagement</button>
      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}

// ─── Footer Editor ──────────────────────────────────────────────

function FooterEditor({ data, onChange, onSave, saving }) {
  const update = (key, val) => onChange({ ...data, [key]: val });
  const updateLink = (idx, key, val) => {
    const links = [...(data.navLinks || [])];
    links[idx] = { ...links[idx], [key]: val };
    update('navLinks', links);
  };
  const addLink = () => update('navLinks', [...(data.navLinks || []), { label: '', href: '#' }]);
  const removeLink = (idx) => update('navLinks', (data.navLinks || []).filter((_, i) => i !== idx));

  return (
    <div className="admin-section" data-testid="admin-footer-editor">
      <h2 className="admin-section-title">Footer</h2>
      <div className="admin-field">
        <label className="admin-label">Tagline</label>
        <input className="admin-input" value={data.tagline || ''} onChange={(e) => update('tagline', e.target.value)} data-testid="footer-tagline" />
      </div>
      <div className="admin-field">
        <label className="admin-label">Social Label</label>
        <input className="admin-input" value={data.socialLabel || ''} onChange={(e) => update('socialLabel', e.target.value)} data-testid="footer-social-label" />
      </div>
      <h3 className="admin-card-title">Navigation Links</h3>
      {(data.navLinks || []).map((link, idx) => (
        <div key={idx} className="admin-row" style={{ alignItems: 'flex-end' }}>
          <div className="admin-field">
            <label className="admin-label">Label</label>
            <input className="admin-input" value={link.label || ''} onChange={(e) => updateLink(idx, 'label', e.target.value)} data-testid={`footer-link-label-${idx}`} />
          </div>
          <div className="admin-field">
            <label className="admin-label">URL</label>
            <input className="admin-input" value={link.href || ''} onChange={(e) => updateLink(idx, 'href', e.target.value)} data-testid={`footer-link-href-${idx}`} />
          </div>
          <button className="admin-remove-btn" onClick={() => removeLink(idx)} style={{ marginBottom: '2px' }} data-testid={`footer-link-remove-${idx}`}>x</button>
        </div>
      ))}
      <button className="admin-add-btn" onClick={addLink} data-testid="footer-add-link">+ Add Link</button>
      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}


// ─── Gallery Manager ────────────────────────────────────────────

function GalleryManager({ token }) {
  const [images, setImages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploads, setUploads] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [orderChanged, setOrderChanged] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const fileInputRef = React.useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL || '';

  const loadGallery = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchGallery(1, 500, false);
      setImages(data.images);
      setTotal(data.total);
      setOrderChanged(false);
    } catch (err) {
      console.error('Failed to load gallery:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadGallery(); }, [loadGallery]);

  const processFiles = async (files) => {
    const validFiles = Array.from(files).filter((f) =>
      ['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(f.type)
    );
    if (validFiles.length === 0) return;

    const newUploads = validFiles.map((file, idx) => ({
      id: `upload-${Date.now()}-${idx}`,
      file,
      progress: 0,
      status: 'pending',
      error: '',
    }));

    setUploads((prev) => [...newUploads, ...prev]);

    for (const upload of newUploads) {
      setUploads((prev) =>
        prev.map((u) => (u.id === upload.id ? { ...u, status: 'uploading' } : u))
      );
      try {
        await uploadGalleryImage(token, upload.file, (pct) => {
          setUploads((prev) =>
            prev.map((u) => (u.id === upload.id ? { ...u, progress: pct } : u))
          );
        });
        setUploads((prev) =>
          prev.map((u) => (u.id === upload.id ? { ...u, status: 'done', progress: 100 } : u))
        );
      } catch (err) {
        setUploads((prev) =>
          prev.map((u) =>
            u.id === upload.id ? { ...u, status: 'error', error: err.message } : u
          )
        );
      }
    }
    loadGallery();
    setTimeout(() => {
      setUploads((prev) => prev.filter((u) => u.status !== 'done'));
    }, 3000);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteGalleryImage(token, deleteConfirm);
      setImages((prev) => prev.filter((img) => img.id !== deleteConfirm));
      setTotal((prev) => prev - 1);
    } catch (err) {
      console.error('Delete failed:', err);
    }
    setDeleteConfirm(null);
  };

  /* -- Drag & Drop Reorder -- */
  const onDragStart = (e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', idx);
    e.currentTarget.style.opacity = '0.4';
  };

  const onDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const onDragOverItem = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (idx !== dragOverIdx) setDragOverIdx(idx);
  };

  const onDropItem = (e, dropIdx) => {
    e.preventDefault();
    e.stopPropagation();
    if (dragIdx === null || dragIdx === dropIdx) return;
    const reordered = [...images];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    setImages(reordered);
    setOrderChanged(true);
    setDragIdx(null);
    setDragOverIdx(null);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      const ids = images.map((img) => img.id);
      await reorderGallery(token, ids);
      setOrderChanged(false);
    } catch (err) {
      console.error('Reorder failed:', err);
    } finally {
      setSaving(false);
    }
  };

  /* -- Rotate (deferred): clicks update local state only.
        Click Save to commit all pending rotations to backend in batch.
        Image preview rotates via CSS transform as you click. -- */
  const [pendingRotations, setPendingRotations] = useState({}); // { imageId: degrees (multiple of 90, mod 360) }
  const [savingRotations, setSavingRotations] = useState(false);
  const [rotateProgress, setRotateProgress] = useState(null); // { current, total, currentName }

  const queueRotation = (imageId, delta) => {
    setPendingRotations((prev) => {
      const current = prev[imageId] || 0;
      let next = (current + delta) % 360;
      if (next < 0) next += 360;
      const updated = { ...prev };
      if (next === 0) delete updated[imageId];
      else updated[imageId] = next;
      return updated;
    });
  };

  const clearPendingForImage = (imageId) => {
    setPendingRotations((prev) => {
      if (!(imageId in prev)) return prev;
      const updated = { ...prev };
      delete updated[imageId];
      return updated;
    });
  };

  const handleSaveRotations = async () => {
    const entries = Object.entries(pendingRotations);
    if (entries.length === 0) return;
    setSavingRotations(true);
    setRotateProgress({ current: 0, total: entries.length, currentName: '' });
    const failed = [];
    for (let i = 0; i < entries.length; i++) {
      const [imageId, degrees] = entries[i];
      const img = images.find((x) => x.id === imageId);
      setRotateProgress({ current: i + 1, total: entries.length, currentName: img?.originalName || imageId });
      try {
        const res = await rotateGalleryImage(token, imageId, degrees);
        const cacheBuster = `?t=${Date.now()}-${i}`;
        setImages((prev) => prev.map((x) =>
          x.id === imageId
            ? { ...x, url: res.url, width: res.width, height: res.height, _cacheBuster: cacheBuster }
            : x
        ));
      } catch (err) {
        console.error(`Rotate failed for ${imageId}:`, err);
        failed.push(imageId);
      }
    }
    setSavingRotations(false);
    setRotateProgress(null);
    if (failed.length === 0) {
      setPendingRotations({});
    } else {
      // Keep failed entries; clear successful ones
      setPendingRotations((prev) => {
        const next = {};
        for (const id of failed) if (id in prev) next[id] = prev[id];
        return next;
      });
      alert(`${failed.length} image(s) failed to save. Try again or reload.`);
    }
  };

  const pendingCount = Object.keys(pendingRotations).length;

  const activeUploads = uploads.filter((u) => u.status === 'uploading' || u.status === 'pending');
  const completedUploads = uploads.filter((u) => u.status === 'done');
  const failedUploads = uploads.filter((u) => u.status === 'error');

  return (
    <div className="admin-section" data-testid="admin-gallery-manager">
      <h2 className="admin-section-title">Our Work ({total} images)</h2>

      {/* Drop Zone */}
      <div
        className={`gallery-dropzone ${dragOver ? 'gallery-dropzone-active' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        data-testid="gallery-dropzone"
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/jpeg,image/png,image/webp,image/gif"
          hidden
          onChange={(e) => { processFiles(e.target.files); e.target.value = ''; }}
          data-testid="gallery-file-input"
        />
        <div className="dropzone-icon">
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <path d="M20 6v20M10 16l10-10 10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M4 28v4a4 4 0 004 4h24a4 4 0 004-4v-4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
        </div>
        <p className="dropzone-text">Drop images here or click to browse</p>
        <p className="dropzone-hint">JPG, PNG, WebP, GIF — Bulk upload supported</p>
      </div>

      {/* Upload Progress List */}
      {uploads.length > 0 && (
        <div className="gallery-upload-list" data-testid="gallery-upload-list">
          {activeUploads.length > 0 && <p className="upload-list-label">Uploading {activeUploads.length} file{activeUploads.length > 1 ? 's' : ''}...</p>}
          {completedUploads.length > 0 && <p className="upload-list-label upload-list-success">{completedUploads.length} uploaded successfully</p>}
          {failedUploads.length > 0 && <p className="upload-list-label upload-list-error">{failedUploads.length} failed</p>}
          {uploads.slice(0, 10).map((u) => (
            <div key={u.id} className={`gallery-upload-item gallery-upload-${u.status}`} data-testid={`upload-item-${u.id}`}>
              <span className="upload-item-name">{u.file.name}</span>
              {u.status === 'uploading' && (
                <div className="upload-item-bar">
                  <div className="upload-item-fill" style={{ width: `${u.progress}%` }} />
                </div>
              )}
              {u.status === 'done' && <span className="upload-item-check">Done</span>}
              {u.status === 'error' && <span className="upload-item-err">{u.error}</span>}
              {u.status === 'pending' && <span className="upload-item-pending">Waiting...</span>}
            </div>
          ))}
        </div>
      )}

      {/* Save Order Bar */}
      {orderChanged && (
        <div className="gallery-save-bar" data-testid="gallery-save-bar">
          <span>Order changed — drag photos to rearrange</span>
          <button className="admin-save-btn" onClick={handleSaveOrder} disabled={saving} data-testid="gallery-save-order-btn">
            {saving ? 'Saving...' : 'Save Order'}
          </button>
        </div>
      )}

      {/* Save Rotations Bar */}
      {pendingCount > 0 && (
        <div className="gallery-save-bar gallery-save-bar-orientation" data-testid="gallery-save-rotation-bar">
          <span>
            {pendingCount} image{pendingCount > 1 ? 's' : ''} with unsaved orientation
            {savingRotations && rotateProgress && ` — saving ${rotateProgress.current} of ${rotateProgress.total}…`}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="admin-remove-btn"
              onClick={() => setPendingRotations({})}
              disabled={savingRotations}
              data-testid="gallery-discard-rotations-btn"
            >
              Discard
            </button>
            <button
              className="admin-save-btn"
              onClick={handleSaveRotations}
              disabled={savingRotations}
              data-testid="gallery-save-rotations-btn"
            >
              {savingRotations ? 'Saving…' : `Save ${pendingCount} Rotation${pendingCount > 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* Reorder hint */}
      {!loading && images.length > 1 && !orderChanged && (
        <p className="gallery-reorder-hint" data-testid="gallery-reorder-hint">Drag and drop to reorder photos</p>
      )}

      {/* Image Grid */}
      {loading ? (
        <p style={{ padding: '20px 0', opacity: 0.5 }}>Loading gallery...</p>
      ) : images.length === 0 ? (
        <p style={{ padding: '20px 0', opacity: 0.5 }}>No images yet. Upload some to get started.</p>
      ) : (
        <div className="gallery-admin-grid" data-testid="gallery-admin-grid">
          {images.map((img, idx) => {
            const pendingDeg = pendingRotations[img.id] || 0;
            const hasPending = pendingDeg !== 0;
            return (
            <div
              className={`gallery-admin-item ${dragOverIdx === idx ? 'gallery-drag-over' : ''} ${dragIdx === idx ? 'gallery-dragging' : ''} ${hasPending ? 'gallery-admin-item-pending' : ''}`}
              key={img.id}
              data-testid={`gallery-admin-item-${img.id}`}
              draggable={!hasPending && !savingRotations}
              onDragStart={(e) => onDragStart(e, idx)}
              onDragEnd={onDragEnd}
              onDragOver={(e) => onDragOverItem(e, idx)}
              onDrop={(e) => onDropItem(e, idx)}
            >
              <img
                src={(img.url.startsWith('/') ? `${API}${img.url}` : img.url) + (img._cacheBuster || '')}
                alt={img.originalName || ''}
                loading="lazy"
                style={pendingDeg ? { transform: `rotate(${pendingDeg}deg)`, transition: 'transform 0.25s ease' } : undefined}
                data-testid={`gallery-admin-img-${img.id}`}
              />
              <div className="gallery-admin-rotate" data-testid={`gallery-rotate-${img.id}`}>
                <button
                  className="gallery-admin-rotate-btn"
                  onClick={(e) => { e.stopPropagation(); queueRotation(img.id, -90); }}
                  disabled={savingRotations}
                  title="Rotate left 90°"
                  data-testid={`gallery-rotate-left-${img.id}`}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8a5 5 0 015-5h3M8 1L11 4 8 7" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                <button
                  className="gallery-admin-rotate-btn"
                  onClick={(e) => { e.stopPropagation(); queueRotation(img.id, 90); }}
                  disabled={savingRotations}
                  title="Rotate right 90°"
                  data-testid={`gallery-rotate-right-${img.id}`}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                    <path d="M13 8a5 5 0 00-5-5H5M8 1L5 4 8 7" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                  </svg>
                </button>
                {hasPending && (
                  <button
                    className="gallery-admin-rotate-btn gallery-admin-rotate-undo"
                    onClick={(e) => { e.stopPropagation(); clearPendingForImage(img.id); }}
                    disabled={savingRotations}
                    title="Undo rotation (revert)"
                    data-testid={`gallery-rotate-undo-${img.id}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M4 4l8 8M12 4l-8 8" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
                )}
              </div>
              {hasPending && (
                <span className="gallery-admin-pending-pill" data-testid={`gallery-pending-pill-${img.id}`}>
                  {pendingDeg}° unsaved
                </span>
              )}
              <button
                className="gallery-admin-delete"
                onClick={(e) => { e.stopPropagation(); setDeleteConfirm(img.id); }}
                data-testid={`gallery-delete-${img.id}`}
                title="Delete image"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>
              </button>
              <span className="gallery-admin-name">{img.originalName || img.filename}</span>
              <span className="gallery-admin-order">{idx + 1}</span>
            </div>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="gallery-delete-modal" data-testid="gallery-delete-modal">
          <div className="gallery-delete-backdrop" onClick={() => setDeleteConfirm(null)} />
          <div className="gallery-delete-dialog">
            <p>Delete this image?</p>
            <div className="gallery-delete-actions">
              <button className="admin-remove-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="admin-save-btn" onClick={handleDeleteConfirm} data-testid="gallery-delete-confirm">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Typography Editor ──────────────────────────────────────────

// Baseline visual style used in the live preview when no override is set.
// Mirrors what the public site renders by default for each element.
const ELEMENT_DEFAULTS = {
  'hero.headline':              { fontFamily: "'Bebas Neue', sans-serif",        fontSize: '3.2rem', fontWeight: 800, lineHeight: 0.95, letterSpacing: '0.01em', textTransform: 'uppercase' },
  'hero.subheadline':           { fontFamily: "'Avenir Next', sans-serif",       fontSize: '1rem',   fontWeight: 400, lineHeight: 1.55 },
  'hero.ctaButtons':            { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.85rem',fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
  'about.title':                { fontFamily: "'Barlow Condensed', sans-serif",  fontSize: '2.2rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.01em' },
  'about.body':                 { fontFamily: "'Avenir Next', sans-serif",       fontSize: '1rem',   fontWeight: 400, lineHeight: 1.65 },
  'about.philosophyLines':      { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.95rem',fontWeight: 500, fontStyle: 'italic', lineHeight: 1.5 },
  'capabilities.title':         { fontFamily: "'Barlow Condensed', sans-serif",  fontSize: '2.2rem', fontWeight: 800, textTransform: 'uppercase' },
  'capabilities.subtitle':      { fontFamily: "'Avenir Next', sans-serif",       fontSize: '1.05rem',fontWeight: 500, lineHeight: 1.4 },
  'capabilities.cardTitle':     { fontFamily: "'Avenir Next', sans-serif",       fontSize: '1.1rem', fontWeight: 700 },
  'capabilities.cardBullets':   { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.9rem', fontWeight: 400, lineHeight: 1.5 },
  'capabilities.statNumber':    { fontFamily: "'Bebas Neue', sans-serif",        fontSize: '2.6rem', fontWeight: 700, lineHeight: 1 },
  'capabilities.statLabel':     { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.78rem',fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' },
  'gallery.title':              { fontFamily: "'Barlow Condensed', sans-serif",  fontSize: '2.2rem', fontWeight: 800, textTransform: 'uppercase' },
  'gallery.overlayTitle':       { fontFamily: "'Avenir Next', sans-serif",       fontSize: '1rem',   fontWeight: 600 },
  'gallery.photoCount':         { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.85rem',fontWeight: 500 },
  'sustainability.title':       { fontFamily: "'Barlow Condensed', sans-serif",  fontSize: '2.2rem', fontWeight: 800, textTransform: 'uppercase' },
  'sustainability.intro':       { fontFamily: "'Avenir Next', sans-serif",       fontSize: '1rem',   fontWeight: 400, lineHeight: 1.65 },
  'sustainability.metricValue': { fontFamily: "'Bebas Neue', sans-serif",        fontSize: '2.6rem', fontWeight: 700, lineHeight: 1 },
  'sustainability.metricLabel': { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.85rem',fontWeight: 500 },
  'sustainability.platformTitle':{fontFamily: "'Avenir Next', sans-serif",       fontSize: '1.3rem', fontWeight: 700, letterSpacing: '0.04em' },
  'sustainability.platformBody':{ fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.9rem', fontWeight: 400, lineHeight: 1.5 },
  'proprietary.title':          { fontFamily: "'Barlow Condensed', sans-serif",  fontSize: '2.2rem', fontWeight: 800, textTransform: 'uppercase' },
  'proprietary.label':          { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.85rem',fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' },
  'proprietary.cardTitle':      { fontFamily: "'Bebas Neue', sans-serif",        fontSize: '2rem',   fontWeight: 700, letterSpacing: '0.02em' },
  'contact.headline':           { fontFamily: "'Barlow Condensed', sans-serif",  fontSize: '2.5rem', fontWeight: 800, lineHeight: 1, textTransform: 'uppercase' },
  'contact.eyebrow':            { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.85rem',fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' },
  'contact.subtitle':           { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.95rem',fontWeight: 400, lineHeight: 1.55 },
  'contact.ctaButton':          { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.85rem',fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
  'footer.navLinks':            { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.85rem',fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em' },
  'footer.tagline':             { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.9rem', fontWeight: 400 },
  'navigation.navLinks':        { fontFamily: "'Avenir Next', sans-serif",       fontSize: '0.85rem',fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em' },
  'navigation.logo':            { fontFamily: "'Avenir Next', sans-serif",       fontSize: '1.1rem', fontWeight: 600 },
};

// Resolve representative preview text for an element using actual content where possible.
function getPreviewText(content, section, elKey) {
  const c = content || {};
  const trim = (s, n = 220) => (s && s.length > n ? s.slice(0, n - 1) + '…' : s);
  const map = {
    'hero.headline':              () => [c.hero?.headlineLine1, c.hero?.headlineLine2, c.hero?.headlineLine3].filter(Boolean).join(' ') || 'Where Experiences Become Influence',
    'hero.subheadline':           () => trim(c.hero?.subheadline) || 'Subheadline preview text rendered with the current typography settings.',
    'hero.ctaButtons':            () => c.hero?.ctaPrimaryText || 'Our Work',
    'about.title':                () => c.about?.title || 'About Circle Studios',
    'about.body':                 () => trim(c.about?.text) || 'About body copy preview rendered with the current typography settings.',
    'about.philosophyLines':      () => c.about?.philosophy?.[0] || 'A guiding principle of our work.',
    'capabilities.title':         () => c.capabilities?.title || 'Capabilities',
    'capabilities.subtitle':      () => c.capabilities?.subtitle || 'End-to-End Execution Across the Experience Lifecycle',
    'capabilities.cardTitle':     () => c.capabilities?.items?.[0]?.title || 'Strategy & Advisory',
    'capabilities.cardBullets':   () => trim(c.capabilities?.items?.[0]?.text, 180) || 'Card body text rendered with the current typography settings.',
    'capabilities.statNumber':    () => `${c.capabilities?.stats?.[0]?.num ?? 250}${c.capabilities?.stats?.[0]?.suffix || '+'}`,
    'capabilities.statLabel':     () => c.capabilities?.stats?.[0]?.label || 'Events Delivered',
    'gallery.title':              () => 'Our Work',
    'gallery.overlayTitle':       () => 'Photography',
    'gallery.photoCount':         () => '1,234 photos',
    'sustainability.title':       () => c.sustainability?.title || 'Sustainability First',
    'sustainability.intro':       () => trim(c.sustainability?.intro) || 'Intro text preview rendered with the current typography settings.',
    'sustainability.metricValue': () => c.sustainability?.impactItems?.[0]?.stat || '85%',
    'sustainability.metricLabel': () => c.sustainability?.impactItems?.[0]?.label || 'Recyclable',
    'sustainability.platformTitle':() => c.sustainability?.platforms?.[0]?.name || 'STIX Modular Exhibit System',
    'sustainability.platformBody':() => trim(c.sustainability?.platforms?.[0]?.tagline, 160) || 'Platform tagline preview text.',
    'proprietary.title':          () => c.proprietary?.sectionTitle || 'Proprietary Engagements',
    'proprietary.label':          () => c.proprietary?.sectionLabel || 'Explore',
    'proprietary.cardTitle':      () => c.proprietary?.cards?.[0]?.name || 'SEICON',
    'contact.headline':           () => (c.contact?.title || 'Ready to build something influential?').replace(/\n/g, ' '),
    'contact.eyebrow':            () => c.contact?.eyebrow || 'Start a Conversation',
    'contact.subtitle':           () => trim(c.contact?.subtitle, 160) || 'Subtitle preview text rendered with the current typography settings.',
    'contact.ctaButton':          () => c.contact?.ctaText || 'Begin Inquiry',
    'footer.navLinks':            () => c.footer?.navLinks?.[0]?.label || 'About',
    'footer.tagline':             () => c.footer?.tagline || 'Circle Studios — Where Experiences Become Influence',
    'navigation.navLinks':        () => 'Capabilities',
    'navigation.logo':            () => 'circle Studios',
  };
  const fn = map[`${section}.${elKey}`];
  return fn ? fn() : 'Preview text';
}

// Merge baseline style + override style for the live preview.
function buildPreviewStyle(baseline, override) {
  const o = override || {};
  const merged = { ...(baseline || {}) };
  if (o.fontFamily) merged.fontFamily = o.fontFamily;
  if (o.fontSize) merged.fontSize = o.fontSize;
  if (o.fontWeight) merged.fontWeight = o.fontWeight;
  if (o.textTransform) merged.textTransform = o.textTransform;
  if (o.letterSpacing) merged.letterSpacing = o.letterSpacing;
  if (o.lineHeight) merged.lineHeight = o.lineHeight;
  return merged;
}

function TypoElementEditor({ value, onChange, onReset, label, previewText, baseline, sectionKey, elementKey }) {
  const v = value || {};
  const set = (key, val) => onChange({ ...v, [key]: val });
  const previewStyle = buildPreviewStyle(baseline, v);
  const hasOverride = Object.values(v).some(Boolean);
  const testIdSlug = `${sectionKey}-${elementKey}`;

  return (
    <div className="admin-card" style={{ padding: '16px', marginBottom: '14px' }} data-testid={`typo-element-${testIdSlug}`}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', gap: '10px' }}>
        <p style={{ fontSize: '0.85rem', fontWeight: 700, color: 'rgba(255,255,255,0.95)', margin: 0, letterSpacing: '0.02em' }}>{label}</p>
        {hasOverride && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '0.6rem', fontWeight: 600, color: '#E87722', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '2px 6px', border: '1px solid rgba(232,119,34,0.4)', borderRadius: '3px' }}>Custom</span>
            <button
              type="button"
              onClick={onReset}
              style={{ background: 'none', border: '1px solid rgba(255,100,100,0.3)', color: 'rgba(255,140,140,0.85)', fontSize: '0.6rem', padding: '2px 8px', borderRadius: '3px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em' }}
              data-testid={`typo-reset-element-${testIdSlug}`}
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {/* LIVE PREVIEW PANE */}
      <div
        data-testid={`typo-preview-${testIdSlug}`}
        style={{
          background: 'linear-gradient(180deg, #0b0b10 0%, #14141d 100%)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '8px',
          padding: '20px 22px',
          marginBottom: '14px',
          minHeight: '70px',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <div style={{ ...previewStyle, color: '#fff', wordBreak: 'break-word', whiteSpace: 'pre-wrap', maxWidth: '100%' }}>
          {previewText}
        </div>
      </div>
      <p style={{ fontSize: '0.62rem', color: 'rgba(255,255,255,0.35)', margin: '-8px 0 12px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        Live preview — updates as you change controls below
      </p>

      {/* CONTROLS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
        <div className="admin-field" style={{ marginBottom: 0 }}>
          <label className="admin-label" style={{ fontSize: '0.65rem' }}>
            Font Family <span style={{ opacity: 0.5, textTransform: 'none', letterSpacing: 0 }}>({FONT_LIBRARY.length} fonts)</span>
          </label>
          <select className="admin-input" value={v.fontFamily || ''} onChange={(e) => set('fontFamily', e.target.value)} style={{ fontSize: '0.78rem', padding: '6px 8px' }} data-testid={`typo-font-family-${testIdSlug}`}>
            <option value="">Default</option>
            {FONT_LIBRARY.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>
        <div className="admin-field" style={{ marginBottom: 0 }}>
          <label className="admin-label" style={{ fontSize: '0.65rem' }}>
            Font Weight <span style={{ opacity: 0.5, textTransform: 'none', letterSpacing: 0 }}>({FONT_WEIGHTS.length} options)</span>
          </label>
          <select className="admin-input" value={v.fontWeight || ''} onChange={(e) => set('fontWeight', e.target.value)} style={{ fontSize: '0.78rem', padding: '6px 8px' }} data-testid={`typo-font-weight-${testIdSlug}`}>
            <option value="">Default</option>
            {FONT_WEIGHTS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </div>
        <div className="admin-field" style={{ marginBottom: 0 }}>
          <label className="admin-label" style={{ fontSize: '0.65rem' }}>Font Size</label>
          <input className="admin-input" value={v.fontSize || ''} onChange={(e) => set('fontSize', e.target.value)} placeholder="e.g. 2rem, 18px" style={{ fontSize: '0.78rem', padding: '6px 8px' }} data-testid={`typo-font-size-${testIdSlug}`} />
        </div>
        <div className="admin-field" style={{ marginBottom: 0 }}>
          <label className="admin-label" style={{ fontSize: '0.65rem' }}>
            Text Transform <span style={{ opacity: 0.5, textTransform: 'none', letterSpacing: 0 }}>({TEXT_TRANSFORMS.length} options)</span>
          </label>
          <select className="admin-input" value={v.textTransform || ''} onChange={(e) => set('textTransform', e.target.value)} style={{ fontSize: '0.78rem', padding: '6px 8px' }} data-testid={`typo-text-transform-${testIdSlug}`}>
            <option value="">Default</option>
            {TEXT_TRANSFORMS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="admin-field" style={{ marginBottom: 0 }}>
          <label className="admin-label" style={{ fontSize: '0.65rem' }}>Letter Spacing</label>
          <input className="admin-input" value={v.letterSpacing || ''} onChange={(e) => set('letterSpacing', e.target.value)} placeholder="e.g. -0.02em, 1px" style={{ fontSize: '0.78rem', padding: '6px 8px' }} data-testid={`typo-letter-spacing-${testIdSlug}`} />
        </div>
        <div className="admin-field" style={{ marginBottom: 0 }}>
          <label className="admin-label" style={{ fontSize: '0.65rem' }}>Line Height</label>
          <input className="admin-input" value={v.lineHeight || ''} onChange={(e) => set('lineHeight', e.target.value)} placeholder="e.g. 1.2, 1.5" style={{ fontSize: '0.78rem', padding: '6px 8px' }} data-testid={`typo-line-height-${testIdSlug}`} />
        </div>
      </div>
    </div>
  );
}

function TypographyEditor({ data, content, onChange, onSave, saving }) {
  const typo = data || {};
  const [activeSection, setActiveSection] = useState('hero');

  // Preload all fonts in the library so the live preview renders any selection accurately.
  useEffect(() => {
    preloadAllFonts();
  }, []);

  const sections = Object.entries(SECTION_TEXT_ELEMENTS);
  const currentElements = SECTION_TEXT_ELEMENTS[activeSection] || [];
  const sectionData = typo[activeSection] || {};

  const updateElement = (elKey, elValue) => {
    const updated = { ...typo, [activeSection]: { ...sectionData, [elKey]: elValue } };
    onChange(updated);
  };

  const resetElement = (elKey) => {
    const updated = { ...typo, [activeSection]: { ...sectionData } };
    delete updated[activeSection][elKey];
    onChange(updated);
  };

  const resetSection = () => {
    const updated = { ...typo };
    delete updated[activeSection];
    onChange(updated);
  };

  const sectionLabels = {
    hero: 'Hero', about: 'About', capabilities: 'Capabilities', gallery: 'Our Work',
    sustainability: 'Sustainability', proprietary: 'Proprietary Engagements',
    contact: 'Contact', footer: 'Footer', navigation: 'Navigation',
  };

  const sectionHasOverrides = Object.values(sectionData).some(
    (el) => el && Object.values(el).some(Boolean)
  );

  return (
    <div className="admin-section" data-testid="admin-typography-editor">
      <h2 className="admin-section-title">Typography Controls</h2>
      <p className="admin-hint">
        Control font family, size, weight, letter spacing, line height, and text transform for every text element on the site, section by section.
        Each control has a <strong style={{ color: 'rgba(255,255,255,0.85)' }}>live preview</strong> showing exactly what your change looks like before you save.
      </p>
      <p className="admin-hint" style={{ marginTop: '-10px', padding: '10px 12px', background: 'rgba(232,119,34,0.08)', border: '1px solid rgba(232,119,34,0.25)', borderRadius: '6px', color: 'rgba(255,210,180,0.85)', fontSize: '0.72rem', lineHeight: 1.5 }}>
        <strong>Tip — responsive sizing:</strong> Typography applies across desktop, tablet, and mobile via inline styles (which beat our CSS). But <code style={{ background: 'rgba(0,0,0,0.35)', padding: '1px 5px', borderRadius: '3px' }}>rem</code> units scale with the root font-size, which is 125% (20px) on desktop (≥1024px) and 100% (16px) on tablet/mobile. This means <code style={{ background: 'rgba(0,0,0,0.35)', padding: '1px 5px', borderRadius: '3px' }}>3rem</code> renders as 60px on desktop and 48px on mobile — that's intentional responsive scaling, <em>not</em> a failed save. For exact pixel-identical sizes across all breakpoints, use <code style={{ background: 'rgba(0,0,0,0.35)', padding: '1px 5px', borderRadius: '3px' }}>px</code> (e.g. <code style={{ background: 'rgba(0,0,0,0.35)', padding: '1px 5px', borderRadius: '3px' }}>48px</code>).
      </p>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '20px' }}>
        {sections.map(([key]) => {
          const hasOverrides = typo[key] && Object.values(typo[key]).some((el) => el && Object.values(el).some(Boolean));
          return (
            <button
              key={key}
              className={`admin-tab-btn ${activeSection === key ? 'admin-tab-active' : ''}`}
              onClick={() => setActiveSection(key)}
              style={{
                fontSize: '0.72rem',
                padding: '7px 14px',
                borderRadius: '4px',
                border: activeSection === key ? '1px solid rgba(150,140,195,0.8)' : '1px solid rgba(255,255,255,0.15)',
                background: activeSection === key ? 'rgba(150,140,195,0.2)' : 'rgba(255,255,255,0.05)',
                color: activeSection === key ? '#fff' : 'rgba(255,255,255,0.6)',
                cursor: 'pointer',
                position: 'relative',
              }}
              data-testid={`typo-section-${key}`}
            >
              {sectionLabels[key] || key}
              {hasOverrides && (
                <span style={{ display: 'inline-block', width: '6px', height: '6px', background: '#E87722', borderRadius: '50%', marginLeft: '6px', verticalAlign: 'middle' }} />
              )}
            </button>
          );
        })}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <h3 style={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255,255,255,0.9)', margin: 0 }}>
          {sectionLabels[activeSection]} <span style={{ opacity: 0.5, fontWeight: 400 }}>— {currentElements.length} text elements</span>
        </h3>
        {sectionHasOverrides && (
          <button
            onClick={resetSection}
            style={{ background: 'none', border: '1px solid rgba(255,100,100,0.4)', color: 'rgba(255,140,140,0.85)', fontSize: '0.65rem', padding: '4px 10px', borderRadius: '3px', cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.06em' }}
            data-testid={`typo-reset-section-${activeSection}`}
          >
            Reset section
          </button>
        )}
      </div>

      {currentElements.map(el => (
        <TypoElementEditor
          key={el.key}
          label={el.label}
          value={sectionData[el.key]}
          onChange={(val) => updateElement(el.key, val)}
          onReset={() => resetElement(el.key)}
          previewText={getPreviewText(content, activeSection, el.key)}
          baseline={ELEMENT_DEFAULTS[`${activeSection}.${el.key}`]}
          sectionKey={activeSection}
          elementKey={el.key}
        />
      ))}

      <SaveButton saving={saving} onSave={onSave} />
    </div>
  );
}
